import {useCallback, useState} from 'react';
import {call, cancelled} from 'redux-saga/effects';
import axios from 'axios';

import {useSagaEffect, waitUntilReady} from '../store/sagas';
import {putFeedback} from '../store/feedback';
import storage from "../utils/storage";

let debug = () => {};
if (process.env.NODE_ENV === 'development') {
    debug = (...args) => {
        // eslint-disable-next-line no-console
        console.log('client.js', ...args);
    }
}

export const apiClient = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL,
    timeout: 60000,
    headers: { common: {
            'X-Api-Platform': 'Scored-Desktop'
    } },
});

export function clearApiCredentials() {
    storage.removeItem('api_key');
    storage.removeItem('api_secret');
    delete apiClient.defaults.headers.common['X-Api-Key'];
    delete apiClient.defaults.headers.common['X-Api-Secret'];
    debug('cleared credentials');
}

export function loadApiCredentials() {
    const key = storage.getItem('api_key');
    const secret = storage.getItem('api_secret');
    if (!key || !secret || key === 'undefined') {
        clearApiCredentials();
        return false;
    }
    Object.assign(apiClient.defaults.headers.common, {
        'X-Api-Key': key,
        'X-Api-Secret': secret,
    });
    debug('loaded credentials');
    return true;
}

export function setApiCredentials(key, secret) {
    storage.setItem('api_key', key);
    storage.setItem('api_secret', secret);
    Object.assign(apiClient.defaults.headers.common, {
        'X-Api-Key': key,
        'X-Api-Secret': secret,
    });
    debug('set credentials');
}

export function* plainRequest(config) {
    const source = axios.CancelToken.source();
    try {
        return yield call([apiClient, 'request'], {
            ...config,
            cancelToken: source.token,
            /* onDownloadProgress(progress) {
                console.log('download progress:', progress);
            } */
        });
    } catch (error) {
        if (axios.isCancel(error)) {
            return null;
        }
        throw error;
    } finally {
        if (yield cancelled()) {
            yield call(source.cancel);
        }
    }
}

export function* apiRequest(config) {
    yield putFeedback({ busy: true });
    try {
        const source = axios.CancelToken.source();
        let response;
        try {
            debug('request', config.method, config.url, Boolean(apiClient.defaults.headers.common['X-Api-Key']));
            response = yield call([apiClient, 'request'], {
                ...config,
                cancelToken: source.token,
                onUploadProgress(progress) {
                    // TODO: inject progress into feedback (using a channel)
                    if (process.env.NODE_ENV === 'development') {
                        // eslint-disable-next-line no-console
                        console.log('upload progress:', progress);
                    }
                },
                /* onDownloadProgress(progress) {
                    console.log('download progress:', progress);
                } */
            });
        } catch (error) {
            if (axios.isCancel(error)) {
                return null;
            }
            throw error;
        } finally {
            if (yield cancelled()) {
                yield call(source.cancel);
            }
        }
        const {data: {status, error, ...data}} = response;
        if (typeof status === 'boolean') {
            // new API status style
            if (status === false) {
                throw new Error(error);
            }
        } else if (typeof status === 'undefined') {
            // eslint-disable-next-line no-console
            console.warn(`missing status on API ${config.url}, assuming success`);
        } else if (status !== 'success') {
            // old API status style
            throw new Error(status);
        }
        return data;
    } finally {
        yield putFeedback({ busy: false });
    }
}

function encodeParams(data) {
    const encodedData = new URLSearchParams();
    for (const [name, value] of Object.entries(data)) {
        encodedData.set(name, value.toString());
    }
    return encodedData;
}

/**
 *
 * @param url:string
 * @param params:object? optional query
 * @returns {Generator<*,object|null>}
 */
export function* apiGet(url, params) {
    yield call(waitUntilReady);
    if (typeof params === 'object') {
        url = `${url}${url.indexOf('?') === -1 ? '?' : '&'}${encodeParams(params).toString()}`;
    }
    return yield call(apiRequest, {method: 'get', url});
}

/**
 *
 * @param url:string
 * @param data:object
 * @returns {Generator<*,object|null>}
 */
export function* apiPost(url, data={}) {
    yield call(waitUntilReady);
    return yield call(apiRequest, {method: 'post', url, data: encodeParams(data)});
}

export function* apiPostWithCaptcha(url, data, recaptcha) {
    try {
        return yield call(apiPost, url, data);
    } catch (error) {
        if (error.message === 'captcha required') {
            let token;
            try {
                token = yield call(recaptcha);
            } catch (e) {
                // eslint-disable-next-line no-alert
                alert(e.toString());
                throw e;
            }
            return yield call(apiPost, url, {...data, "captcha-response": token});
        } 
        throw error;
    }
}

export function* apiPostForm(url, data) {
    yield call(waitUntilReady);
    const encodedData = new FormData();
    for (const [name, value] of Object.entries(data)) {
        encodedData.set(name, value);
    }
    return yield call(apiRequest, {method: 'post', url, data: encodedData});
}

export function* apiPostJSON(url, data) {
    yield call(waitUntilReady);
    return yield call(apiRequest, {method: 'post', url, data});
}

export function* apiExternalPostForm(baseUrl, url, data) {
    yield call(waitUntilReady);
    const encodedData = new FormData();
    for (const [name, value] of Object.entries(data)) {
        encodedData.set(name, value);
    }
    return yield call(apiRequest, {baseURL: baseUrl, method: 'post', url, data: encodedData});
}

export function* apiExternalGet(baseUrl, url, data) {
    yield call(waitUntilReady);
    return yield call(apiRequest, {baseURL: baseUrl, method: 'get', url, data});
}

export const useSagaResult = (feedbackCh, saga) => {
    const [data, setData] = useState(null);
    useSagaEffect(feedbackCh, useCallback(function* () {
        setData(yield call(saga));
    }, [saga]));
    return data;
};

export const useGetRequestData = (feedbackCh, url) =>
    useSagaResult(feedbackCh, useCallback(function* () {
        if (!url) {
            return null;
        }
        return yield call(apiGet, url);
    }, [url]));